import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  Shield,
  Bell,
  TrendingUp,
  Users,
  Check,
  Star,
  ArrowRight,
  Play,
  Pause,
  Clock,
  DollarSign,
  AlertTriangle,
  Zap,
  BarChart3,
  Calendar,
  MessageSquare,
  Lock,
  Target,
  TrendingDown,
  ChevronDown,
  Sparkles,
  BadgeCheck,
  Activity,
  RefreshCw,
  Rocket,
  Mail,
  CheckCircle,
  Loader2,
} from "lucide-react";

const brand = {
  navy: "#172A3A",
  slate: "#20344A",
  gold: "#FFB800",
  gradFrom: "#0f172a",
  gradTo: "#1e3a5f",
  emerald: "#10B981",
  red: "#EF4444",
};

// Smooth scroll handler
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// Animated counter hook
function useCounter(end, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(!startOnView);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startOnView]);

  useEffect(() => {
    if (!started) return;
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end, duration]);

  return { count, ref };
}

// Container component
const Container = ({ children, className = "" }) => (
  <div className={`max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

// API base URL
const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

// Waitlist Form Component
const WaitlistForm = ({ variant = "default" }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    agencySize: "",
    currentCRM: "",
  });
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch(`${API_BASE}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", phone: "", agencySize: "", currentCRM: "" });
      } else {
        setStatus("error");
        setErrorMessage(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage("Network error. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div className={`rounded-2xl p-8 text-center ${variant === "hero" ? "bg-white/10 backdrop-blur" : "bg-emerald-500/10 border border-emerald-500/20"}`}>
        <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-2">You're on the list!</h3>
        <p className="text-white/70">
          Thanks for your interest in Pro IRP. We'll be in touch before our April 2026 launch with early access details.
        </p>
      </div>
    );
  }

  const inputClass = variant === "hero"
    ? "w-full bg-white/10 backdrop-blur border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
    : "w-full bg-slate-800/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <input
          type="text"
          placeholder="Your Name *"
          required
          className={inputClass}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <input
          type="email"
          placeholder="Email Address *"
          required
          className={inputClass}
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input
          type="tel"
          placeholder="Phone Number (optional)"
          className={inputClass}
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        />
        <select
          className={inputClass}
          value={formData.agencySize}
          onChange={(e) => setFormData({ ...formData, agencySize: e.target.value })}
        >
          <option value="">Book Size (optional)</option>
          <option value="1-50">1-50 clients</option>
          <option value="51-200">51-200 clients</option>
          <option value="201-500">201-500 clients</option>
          <option value="501-1000">501-1000 clients</option>
          <option value="1000+">1000+ clients</option>
        </select>
      </div>
      <input
        type="text"
        placeholder="Current CRM (optional)"
        className={inputClass}
        value={formData.currentCRM}
        onChange={(e) => setFormData({ ...formData, currentCRM: e.target.value })}
      />

      {status === "error" && (
        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: brand.gold,
          color: brand.navy,
          boxShadow: `0 20px 40px -10px ${brand.gold}40`,
        }}
      >
        {status === "loading" ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Joining...
          </>
        ) : (
          <>
            Join the Waitlist
            <ArrowRight className="w-5 h-5" />
          </>
        )}
      </button>

      <p className="text-center text-white/50 text-sm">
        Be first in line for our April 2026 launch
      </p>
    </form>
  );
};

export default function WaitlistLanding() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const buildVersion = "v103";


  // Screenshot slideshow
  const shots = useMemo(
    () => [
      { src: "/img/screens/01-dashboard.png", label: "Smart Dashboard" },
      { src: "/img/screens/client-risk.png", label: "Risk Scoring" },
      { src: "/img/screens/aep-wizard.png", label: "AEP Wizard" },
      { src: "/img/screens/oep-hub.png", label: "OEP Hub" },
      { src: "/img/screens/05-automations.png", label: "Automations" },
    ],
    []
  );

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((i) => (i + 1) % shots.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isPlaying, shots.length]);


  // FAQ data
  const faqs = [
    {
      q: "When will Pro IRP launch?",
      a: "We're planning to launch in April 2026. Join the waitlist to be among the first to get access and receive exclusive early-bird benefits.",
    },
    {
      q: "How does the CMS Blue Button integration work?",
      a: "Your clients authorize access through Medicare.gov's secure Blue Button portal. Once connected, we receive their claims, prescriptions, and provider data automatically. You'll see health changes, coverage gaps, and potential issues before they become problems.",
    },
    {
      q: "Is my clients' data secure and HIPAA compliant?",
      a: "Absolutely. We use AES-256 encryption on AWS infrastructure and maintain full HIPAA compliance with BAA support. Every data access is logged for audit trails. We take PHI protection as seriously as you do.",
    },
    {
      q: "Can I import my existing client list?",
      a: "Yes! Upload a CSV or Excel file with your book of business. Our smart mapper handles different column formats automatically. Most agents are fully imported within 15 minutes.",
    },
    {
      q: "How accurate is the churn prediction?",
      a: "Our AI model analyzes 47 different signals including health changes, engagement patterns, plan fit, and seasonal factors. In back-testing across 50,000+ client records, we identified 73% of churned clients at least 30 days before they left.",
    },
    {
      q: "Is there a cost to join the waitlist?",
      a: "No! Joining the waitlist is completely free. You'll just be among the first to know when we launch and may receive special early-access pricing.",
    },
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div
      className="min-h-screen"
      style={{
        background: `linear-gradient(135deg, ${brand.gradFrom} 0%, ${brand.gradTo} 50%, ${brand.gradFrom} 100%)`,
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
      }}
    >
      {/* ==================== NAVIGATION ==================== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-white/5">
        <Container>
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="Pro IRP"
                className="w-10 h-10 rounded-full bg-white shadow-lg"
              />
              <span className="font-extrabold text-white text-xl tracking-tight">
                Pro <span style={{ color: brand.gold }}>IRP</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              <button
                onClick={() => scrollTo("features")}
                className="text-white/80 hover:text-white transition font-medium"
              >
                Features
              </button>
              <button
                onClick={() => scrollTo("how-it-works")}
                className="text-white/80 hover:text-white transition font-medium"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollTo("waitlist")}
                className="px-6 py-2.5 rounded-full font-bold shadow-lg transition transform hover:scale-105 hover:shadow-xl"
                style={{ background: brand.gold, color: brand.navy }}
              >
                Join Waitlist
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    mobileMenuOpen
                      ? "M6 18L18 6M6 6l12 12"
                      : "M4 6h16M4 12h16M4 18h16"
                  }
                />
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden pb-4 border-t border-white/10 pt-4">
              <div className="flex flex-col gap-4">
                <button
                  onClick={() => {
                    scrollTo("features");
                    setMobileMenuOpen(false);
                  }}
                  className="text-white/80 hover:text-white transition text-left"
                >
                  Features
                </button>
                <button
                  onClick={() => {
                    scrollTo("how-it-works");
                    setMobileMenuOpen(false);
                  }}
                  className="text-white/80 hover:text-white transition text-left"
                >
                  How It Works
                </button>
                <button
                  onClick={() => {
                    scrollTo("waitlist");
                    setMobileMenuOpen(false);
                  }}
                  className="px-6 py-2.5 rounded-full font-bold text-center"
                  style={{ background: brand.gold, color: brand.navy }}
                >
                  Join Waitlist
                </button>
              </div>
            </div>
          )}
        </Container>
      </header>

      {/* ==================== HERO SECTION ==================== */}
      <section className="pt-28 pb-12 lg:pt-36 lg:pb-20 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl opacity-20"
            style={{ background: brand.gold }}
          />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-10 bg-blue-500" />
        </div>

        <Container className="relative">
          {/* Launch Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 backdrop-blur">
              <Rocket className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-semibold">
                Launching April 2026
              </span>
              <span className="text-white/60">|</span>
              <span className="text-white/80 text-sm">
                Join the waitlist for early access
              </span>
            </div>
          </div>

          {/* Main Hero Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Copy */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] tracking-tight">
                Stop Losing Clients You{" "}
                <span
                  className="relative inline-block"
                  style={{ color: brand.gold }}
                >
                  Could Have Saved
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    viewBox="0 0 200 12"
                    fill="none"
                  >
                    <path
                      d="M2 8c50-6 100-6 150-2s46 4 46 4"
                      stroke={brand.gold}
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeOpacity="0.4"
                    />
                  </svg>
                </span>
              </h1>

              <p className="mt-6 text-lg sm:text-xl text-white/70 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                The average Medicare agent loses{" "}
                <span className="text-white font-semibold">
                  18-22% of clients
                </span>{" "}
                every year. Pro IRP uses real-time health data and AI-powered
                predictions to help you{" "}
                <span className="text-white font-semibold">
                  retain more clients
                </span>{" "}
                than ever before.
              </p>

              {/* Trust Indicators */}
              <div className="mt-8 flex flex-wrap gap-6 justify-center lg:justify-start text-sm text-white/60">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  CMS Blue Button Integration
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  AI Churn Prediction
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  HIPAA Compliant
                </div>
              </div>

              {/* CTA - Scroll to waitlist */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => scrollTo("waitlist")}
                  className="group px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition transform hover:scale-105 flex items-center justify-center gap-2"
                  style={{
                    background: brand.gold,
                    color: brand.navy,
                    boxShadow: `0 20px 40px -10px ${brand.gold}40`,
                  }}
                >
                  Join the Waitlist
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Right: Product Screenshot Showcase */}
            <div className="relative">
              {/* Main Screenshot */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-slate-800">
                <div className="relative" style={{ aspectRatio: "16/10" }}>
                  {shots.map((shot, i) => (
                    <img
                      key={shot.src}
                      src={shot.src}
                      alt={shot.label}
                      className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
                      style={{
                        opacity: i === currentSlide ? 1 : 0,
                        transform:
                          i === currentSlide ? "scale(1)" : "scale(1.05)",
                      }}
                    />
                  ))}
                </div>

                {/* Slide Controls */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5">
                    <button
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="text-white/80 hover:text-white transition"
                    >
                      {isPlaying ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </button>
                    <span className="text-white text-sm font-medium">
                      {shots[currentSlide].label}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    {shots.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentSlide(i)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          i === currentSlide
                            ? "bg-white w-6"
                            : "bg-white/40 hover:bg-white/60"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Stats Cards */}
              <div className="absolute -left-4 top-8 bg-white rounded-xl shadow-xl p-4 animate-pulse-slow hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">
                      Retention Rate
                    </div>
                    <div className="text-lg font-bold text-slate-900">
                      +31% avg
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 bottom-20 bg-white rounded-xl shadow-xl p-4 animate-pulse-slow hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">
                      At-Risk Alert
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      Martha J. - High Risk
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="mt-16 flex justify-center">
            <button
              onClick={() => scrollTo("problem")}
              className="text-white/40 hover:text-white/60 transition animate-bounce"
            >
              <ChevronDown className="w-8 h-8" />
            </button>
          </div>
        </Container>
      </section>

      {/* ==================== PROBLEM SECTION ==================== */}
      <section id="problem" className="py-20 bg-slate-900/50">
        <Container>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-red-500/10 text-red-400 border border-red-500/20 mb-4">
              <AlertTriangle className="w-4 h-4" />
              The Problem
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Client Churn is Killing Your Business
            </h2>
            <p className="mt-4 text-lg text-white/60 max-w-2xl mx-auto">
              Every year, Medicare agents silently lose thousands in commissions
              to preventable client churn.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                stat: "20%",
                label: "Average annual churn",
                desc: "1 in 5 clients leaves every year",
                icon: TrendingDown,
                color: "red",
              },
              {
                stat: "$5,400",
                label: "Lost per churned client",
                desc: "Average lifetime commission value",
                icon: DollarSign,
                color: "orange",
              },
              {
                stat: "67%",
                label: "Never saw it coming",
                desc: "Agents blindsided by client departures",
                icon: AlertTriangle,
                color: "yellow",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-white/5 p-6"
              >
                <div
                  className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20 bg-${item.color}-500`}
                />
                <item.icon
                  className={`w-10 h-10 mb-4 text-${item.color}-400`}
                />
                <div className="text-4xl font-extrabold text-white mb-1">
                  {item.stat}
                </div>
                <div className="font-semibold text-white/90">{item.label}</div>
                <div className="text-sm text-white/50 mt-1">{item.desc}</div>
              </div>
            ))}
          </div>

          {/* Before/After Comparison */}
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="rounded-2xl bg-gradient-to-br from-red-900/20 to-slate-900/50 border border-red-500/20 p-8">
              <div className="flex items-center gap-2 text-red-400 font-bold mb-6">
                <TrendingDown className="w-5 h-5" />
                WITHOUT Pro IRP
              </div>
              <ul className="space-y-4">
                {[
                  "Clients leave without warning",
                  "Manual tracking in spreadsheets",
                  "No visibility into health changes",
                  "Reactive instead of proactive",
                  "Hours spent on AEP/OEP prep",
                  "Missed renewal opportunities",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/70">
                    <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-red-400 text-xs">✕</span>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* After */}
            <div className="rounded-2xl bg-gradient-to-br from-emerald-900/20 to-slate-900/50 border border-emerald-500/20 p-8">
              <div className="flex items-center gap-2 text-emerald-400 font-bold mb-6">
                <TrendingUp className="w-5 h-5" />
                WITH Pro IRP
              </div>
              <ul className="space-y-4">
                {[
                  "30-day early warning on at-risk clients",
                  "Automated tracking and prioritization",
                  "Real-time Blue Button health insights",
                  "Proactive outreach before problems",
                  "AEP/OEP prep in days, not weeks",
                  "Never miss another renewal",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/70">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>


      {/* ==================== FEATURES SECTION ==================== */}
      <section id="features" className="py-20 bg-slate-900/50">
        <Container>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 mb-4">
              <Sparkles className="w-4 h-4" />
              Powerful Features
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Everything You Need to Retain More Clients
            </h2>
            <p className="mt-4 text-lg text-white/60 max-w-2xl mx-auto">
              Built specifically for Medicare agents who are serious about
              protecting their book of business.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Heart,
                title: "CMS Blue Button Integration",
                desc: "Connect to your clients' real Medicare claims data. See prescriptions, providers, diagnoses, and coverage gaps in real-time.",
                color: "red",
                badge: "UNIQUE",
              },
              {
                icon: BarChart3,
                title: "AI Churn Prediction",
                desc: "Our 47-signal model identifies at-risk clients 30+ days before they leave. Know exactly who needs attention and why.",
                color: "purple",
                badge: "AI-POWERED",
              },
              {
                icon: Calendar,
                title: "AEP & OEP Wizard",
                desc: "Automated countdown sequences, bulk outreach, and smart prioritization. Prep for enrollment periods in days, not weeks.",
                color: "blue",
              },
              {
                icon: MessageSquare,
                title: "Smart Outreach",
                desc: "Automated email and SMS sequences for birthdays, anniversaries, renewals, and health changes. Set it and forget it.",
                color: "green",
              },
              {
                icon: Shield,
                title: "HIPAA Compliant",
                desc: "AES-256 encryption. Full audit trails. BAA support for agencies. Your clients' data is safe and secure.",
                color: "cyan",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="group relative rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/5 p-6 hover:border-white/10 transition-all hover:transform hover:scale-[1.02]"
              >
                {feature.badge && (
                  <div
                    className="absolute -top-3 right-4 px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{ background: brand.gold, color: brand.navy }}
                  >
                    {feature.badge}
                  </div>
                )}
                <div
                  className={`w-12 h-12 rounded-xl bg-${feature.color}-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <feature.icon className={`w-6 h-6 text-${feature.color}-400`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-white/60 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Blue Button Feature Highlight */}
          <div className="mt-16 rounded-3xl bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-blue-600/20 border border-blue-500/20 p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-500/20 text-blue-400 mb-4">
                  <BadgeCheck className="w-4 h-4" />
                  Featured Integration
                </div>
                <h3 className="text-2xl lg:text-3xl font-extrabold text-white mb-4">
                  CMS Blue Button Integration
                </h3>
                <p className="text-white/70 text-lg mb-6 leading-relaxed">
                  Most agents find out about client health changes{" "}
                  <span className="text-white font-semibold">after</span> they've
                  already switched plans. With Blue Button integration, you'll
                  know about new diagnoses, medication changes, and
                  hospitalizations{" "}
                  <span className="text-white font-semibold">
                    as soon as they happen
                  </span>
                  .
                </p>
                <ul className="space-y-3">
                  {[
                    "Real-time prescription monitoring",
                    "Provider network analysis",
                    "Coverage gap detection",
                    "Hospitalization alerts",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 text-white/80"
                    >
                      <Check className="w-5 h-5 text-blue-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img
                  src="/img/screens/client-risk.png"
                  alt="Blue Button Integration"
                  className="rounded-xl shadow-2xl border border-white/10"
                />
                <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 hidden lg:block">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">New Alert</div>
                      <div className="text-sm font-semibold text-slate-900">
                        Rx change detected
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section id="how-it-works" className="py-20">
        <Container>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 mb-4">
              <Zap className="w-4 h-4" />
              Quick Start
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Up and Running in 10 Minutes
            </h2>
            <p className="mt-4 text-lg text-white/60 max-w-2xl mx-auto">
              Simple setup process. Import your clients and start seeing value
              immediately.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-16 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            {[
              {
                step: "1",
                title: "Import Your Clients",
                desc: "Upload a CSV or Excel file. Our smart mapper handles any format.",
                icon: Users,
                time: "2 min",
              },
              {
                step: "2",
                title: "Connect Blue Button",
                desc: "Clients authorize access through Medicare.gov's secure portal.",
                icon: Heart,
                time: "5 min",
              },
              {
                step: "3",
                title: "Get Risk Scores",
                desc: "AI analyzes 47 signals to identify at-risk clients instantly.",
                icon: BarChart3,
                time: "Instant",
              },
              {
                step: "4",
                title: "Take Action",
                desc: "Prioritized call lists and automated outreach do the work.",
                icon: Target,
                time: "Daily",
              },
            ].map((item, i) => (
              <div key={i} className="text-center relative">
                <div className="relative inline-flex">
                  <div
                    className="w-32 h-32 rounded-2xl flex items-center justify-center mx-auto mb-6 relative overflow-hidden"
                    style={{ background: `${brand.gold}15` }}
                  >
                    <item.icon
                      className="w-12 h-12 relative z-10"
                      style={{ color: brand.gold }}
                    />
                  </div>
                  <div
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                    style={{ background: brand.gold, color: brand.navy }}
                  >
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-white/60 text-sm mb-2">{item.desc}</p>
                <div className="inline-flex items-center gap-1 text-xs text-white/40">
                  <Clock className="w-3 h-3" />
                  {item.time}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ==================== WAITLIST SECTION ==================== */}
      <section id="waitlist" className="py-20">
        <Container>
          <div className="max-w-3xl mx-auto">
            <div className="rounded-3xl bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-white/10 overflow-hidden">
              <div
                className="h-2"
                style={{ background: brand.gold }}
              />
              <div className="p-8 lg:p-12">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
                    <Rocket className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-400 font-semibold">
                      Launching April 2026
                    </span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                    Be First in Line
                  </h2>
                  <p className="text-white/60 text-lg max-w-xl mx-auto">
                    Join the waitlist to get early access, exclusive launch pricing,
                    and be among the first Medicare agents to use Pro IRP.
                  </p>
                </div>

                <WaitlistForm />

                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex flex-wrap items-center justify-center gap-6 text-white/50 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      We'll never spam you
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Your info is secure
                    </div>
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Unsubscribe anytime
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ==================== FAQ SECTION ==================== */}
      <section className="py-20 bg-slate-900/50">
        <Container>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-white">
                Frequently Asked Questions
              </h2>
              <p className="mt-4 text-white/60">
                Everything you need to know about Pro IRP
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-slate-800/50 border border-white/5 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold text-white pr-4">
                      {faq.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-white/50 transition-transform flex-shrink-0 ${
                        openFaq === i ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 text-white/60 leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ==================== TRUST/COMPLIANCE SECTION ==================== */}
      <section className="py-16">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
            {[
              { icon: Shield, label: "HIPAA Compliant" },
              { icon: Lock, label: "AES-256 Encryption" },
              { icon: BadgeCheck, label: "BAA Support" },
              { icon: RefreshCw, label: "99.9% Uptime" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 text-white/50"
              >
                <item.icon className="w-6 h-6" />
                <span className="font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-20">
        <Container>
          <div className="relative rounded-3xl overflow-hidden">
            {/* Background */}
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, ${brand.navy} 0%, ${brand.slate} 100%)`,
              }}
            />
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: `radial-gradient(circle at 30% 50%, ${brand.gold}30 0%, transparent 50%)`,
              }}
            />

            <div className="relative px-8 py-16 lg:py-24 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
                <Rocket className="w-5 h-5 text-amber-400" />
                <span className="text-amber-400 font-semibold">
                  April 2026 Launch
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6 max-w-3xl mx-auto leading-tight">
                Don't Get Left Behind When We Launch
              </h2>
              <p className="text-xl text-white/70 max-w-2xl mx-auto mb-10">
                Join hundreds of Medicare agents already on the waitlist.
                Be the first to access the most powerful retention platform ever built for Medicare.
              </p>

              <button
                onClick={() => scrollTo("waitlist")}
                className="group px-10 py-5 rounded-xl font-bold text-xl shadow-2xl transition transform hover:scale-105 flex items-center justify-center gap-3 mx-auto"
                style={{
                  background: brand.gold,
                  color: brand.navy,
                  boxShadow: `0 25px 50px -10px ${brand.gold}50`,
                }}
              >
                Join the Waitlist Now
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="mt-8 flex flex-wrap gap-6 justify-center text-sm text-white/60">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  Free to join
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  Early access benefits
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  No commitment
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="py-12 border-t border-white/5">
        <Container>
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/logo.png"
                  alt="Pro IRP"
                  className="w-10 h-10 rounded-full bg-white"
                />
                <span className="font-extrabold text-white text-xl">
                  Pro <span style={{ color: brand.gold }}>IRP</span>
                </span>
              </div>
              <p className="text-white/50 text-sm leading-relaxed">
                The intelligent retention platform for Medicare agents who are
                serious about protecting their book of business.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30">
                <Rocket className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">
                  Launching April 2026
                </span>
              </div>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-bold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-white/50">
                <li>
                  <button
                    onClick={() => scrollTo("features")}
                    className="hover:text-white transition"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollTo("how-it-works")}
                    className="hover:text-white transition"
                  >
                    How It Works
                  </button>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-white/50">
                <li>
                  <a
                    href="mailto:hello@proirp.com"
                    className="hover:text-white transition"
                  >
                    Contact Us
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white transition">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Get Started */}
            <div>
              <h4 className="font-bold text-white mb-4">Get Started</h4>
              <ul className="space-y-2 text-white/50">
                <li>
                  <button
                    onClick={() => scrollTo("waitlist")}
                    className="hover:text-white transition"
                  >
                    Join Waitlist
                  </button>
                </li>
                <li>
                  <a
                    href="mailto:hello@proirp.com"
                    className="hover:text-white transition"
                  >
                    Partner With Us
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white/40 text-sm">
              &copy; {new Date().getFullYear()} Pro IRP. All rights reserved.
            </div>
            <div className="flex items-center gap-4 md:gap-6 text-white/40 text-sm flex-wrap justify-center">
              <Link to="/privacy" className="hover:text-white transition">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-white transition">
                Terms of Service
              </Link>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                HIPAA Compliant
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                256-bit SSL
              </div>
            </div>
          </div>
        </Container>
      </footer>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
